import * as React from 'react'
import { redirect } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import {
  ContactsDetailsPageRouteDocument,
  QuotesTabsVisibilityDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { Navigation } from 'modules/core/lib/navigation/context'
import { NavigationPaths } from 'modules/core/lib/navigation/context'
import type { RouteObjectWithMeta } from 'modules/core/lib/navigation/routes'
import { buildRoute } from 'modules/core/lib/react-router'

import { ContactHeader } from 'modules/finance/components/Contacts/ContactHeader'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { buildQuoteHeaderProps } from 'modules/finance/routes/ResourceHeaders/quote_header'
import VendorsListPage from 'modules/finance/pages/VendorsListPage'

const ContactEditPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactEditPage')
)

const ClientsListPage = React.lazy(() => import('modules/finance/pages/ClientsListPage'))

const ContactsDetailsOverviewPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsOverviewPage')
)

const ContactsDetailsProjectsPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsProjectsPage')
)

const ContactsDetailsPurchaseOrdersPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsPurchaseOrdersPage')
)

const ContactsDetailsInvoicesPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsInvoicesPage')
)

const ContactsDetailsDocumentsPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsDocumentsPage')
)

const ContactsDetailsDealsPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsDealsPage')
)

const ContactsDetailsQuotesPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsQuotesPage')
)

const ContactsDetailsSaleInvoicesPage = React.lazy(
  () => import('modules/finance/pages/ContactsDetailsPage/ContactsDetailsSaleInvoicesPage')
)

const QuoteDetailsPage = React.lazy(() => import('modules/finance/pages/QuoteDetailsPage'))
const QuotePricingPage = React.lazy(() => import('modules/finance/pages/QuotePricingPage'))

type ContactsRoutesFn = (
  navigation: Navigation<NavigationPaths>,
  contactType: FinanceValueObjectsContactTypeEnum
) => RouteObjectWithMeta[]

export const contactsRoutes: ContactsRoutesFn = (navigation, contactType) => {
  const navigationRoot =
    contactType == 'vendor' ? navigation.finance.spending.vendors : navigation.finance.sales.clients

  const clientQuoteRoute: RouteObjectWithMeta = buildRoute({
    path: navigation.finance.sales.clients.contactId.quotes.quoteId.path,
    handle: {
      resource: true,
      meta: ({ params, watchQuery, session }) =>
        watchQuery({
          query: QuotesTabsVisibilityDocument,
          fetchPolicy: 'cache-and-network',
          variables: {
            id: params['quoteId'] ?? '',
          },
        }).map(({ data }) => {
          const quote = data?.finance?.quote
          const canSeeProjects =
            !!data?.projectManagement?._policies?.sidebarSeeProjectsTopLevelItem?.can
          const authorized = !!quote?._policies?.manage?.can
          const taxBridgeActivations = data?.finance?.taxBridgeActivations ?? []
          const needsLegalEntityActivation =
            quote?.legalEntity?.country === 'es' &&
            !taxBridgeActivations.some(
              (activation) =>
                activation.legalEntityId === quote.legalEntity?.id && activation.activatedAt
            )
          const canCreateProjectQuotes = !!data?.finance?._policies?.quotesCreateProjectQuotes?.can

          if (!quote) return { title: '' }

          return {
            title: quote?.quoteNumber || '-',
            ancestors: [
              {
                id: 'quotes-list',
                title: i18n.t('finance.contacts.contact_details.tabs.quotes'),
                to: navigation.finance.sales.clients.contactId.quotes.fullPath({
                  contactId: params['contactId'] ?? '',
                }),
              },
            ],
            headerComponentProps: buildQuoteHeaderProps(
              canSeeProjects,
              session.access.locale,
              quote,
              needsLegalEntityActivation,
              'client',
              canCreateProjectQuotes
            ),
            subNavigation: [
              {
                id: 'pricing',
                title: i18n.t('finance.quotes.tabs.scope_and_pricing'),
                href: navigation.finance.sales.clients.contactId.quotes.quoteId.pricing.fullPath({
                  contactId: params['contactId'] ?? '',
                  quoteId: params['quoteId'] ?? '',
                }),
                isVisible: authorized,
              },
              {
                id: 'details',
                title: i18n.t('finance.quotes.tabs.quote_details'),
                href: navigation.finance.sales.clients.contactId.quotes.quoteId.details.fullPath({
                  contactId: params['contactId'] ?? '',
                  quoteId: params['quoteId'] ?? '',
                }),
                isVisible: authorized,
              },
            ],
          }
        }),
    },
    children: [
      {
        index: true,
        loader: ({ params }) => {
          return redirect(
            navigation.finance.sales.clients.contactId.quotes.quoteId.pricing.fullPath({
              contactId: String(params.contactId),
              quoteId: String(params.quoteId),
            })
          )
        },
      },
      {
        path: navigation.finance.sales.clients.contactId.quotes.quoteId.pricing.path,
        element: <QuotePricingPage />,
      },
      {
        path: navigation.finance.sales.clients.contactId.quotes.quoteId.details.path,
        element: <QuoteDetailsPage />,
      },
    ],
  })

  const contactsRootRoute: RouteObjectWithMeta = {
    path: navigationRoot.path,
    handle: {
      meta: () => ({
        title:
          contactType == 'vendor'
            ? i18n.t('finance.modules.spending.vendors')
            : i18n.t('finance.modules.sales.clients'),
      }),
    },
    children: [
      {
        index: true,
        element: contactType == 'vendor' ? <VendorsListPage /> : <ClientsListPage />,
      },
      {
        path: navigationRoot.contactId.path,
        handle: {
          resource: true,
          meta: ({ params, watchQuery, session }) =>
            watchQuery({
              query: ContactsDetailsPageRouteDocument,
              variables: {
                id: String(params['contactId']),
                companyId: session.company.id,
                featureAvailabilityIds: [
                  Features.projects_management,
                  Features.projects_clients,
                  Features.sales_deals,
                  Features.sales_quotes_management,
                  Features.sales_invoices_management,
                ],
              },
            }).map(({ data }) => {
              const contact = data?.finance.contact
              const contactName = contact
                ? contactFullName(contact)
                : i18n.t('finance.contacts.title')
              const canRequestUpsell = data?.upsell?._policies?.canRequestUpsell?.can ?? false

              const seeProjectsPage =
                data?.projectManagement._policies.pagesSeeProjectsRoot.can ?? false

              const canManagePurchaseFinancialDocuments =
                data?.finance._policies.financialDocumentsManagePurchase.can ?? false
              const canSeePurchaseOrders =
                data?.procurement._policies.purchaseOrderRead.can ?? false

              const hasDealsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                (feature) => feature.id === Features.sales_deals
              )?.available
              const canSeeDealsUpsell = !hasDealsFeature && canRequestUpsell
              const showDealsTab =
                contactType === 'client' &&
                (canSeeDealsUpsell ||
                  data?.finance._policies.sidebarSeeFinanceDealsSubLevelItem.can)

              const hasQuotesFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                (feature) => feature.id === Features.sales_quotes_management
              )?.available
              const canSeeQuotesUpsell = !hasQuotesFeature && canRequestUpsell
              const showQuotesTab =
                contactType === 'client' &&
                (canSeeQuotesUpsell ||
                  data?.finance._policies.sidebarSeeFinanceQuotesSubLevelItem.can)

              const hasSaleInvoicesFeature =
                !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                  (feature) => feature.id === Features.sales_invoices_management
                )?.available
              const isSpanishCompany =
                session.company?.legalEntities.find((legalEntity) => legalEntity.main)?.country ===
                'es' // Harcoding this country cause we know we dont sell invoices in other countries so upsell wouldnt make sense
              const canSeeSaleInvoicesUpsell =
                !hasSaleInvoicesFeature && canRequestUpsell && isSpanishCompany

              const showSaleInvoicesTab =
                contactType === 'client' &&
                (canSeeSaleInvoicesUpsell ||
                  data?.finance._policies.financialDocumentsManageSales.can)

              const hasProjectsManagementFeature =
                !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                  (feature) => feature.id === Features.projects_management
                )?.available
              const hasClientFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                (feature) => feature.id === Features.projects_clients
              )?.available

              // When they dont have projects module at all
              const seeProjectsUpsell = !hasProjectsManagementFeature
              // When they have some projects features
              const seeProjectsUpgrade = hasProjectsManagementFeature && !hasClientFeature
              const canSeeProjectsPageContent = seeProjectsPage && hasClientFeature
              const canSeeProjectsPageAsUpsell =
                canRequestUpsell && (seeProjectsUpsell || seeProjectsUpgrade)

              const canSeeProjectsPage = canSeeProjectsPageAsUpsell || canSeeProjectsPageContent

              return {
                title: contactName,
                headerComponent: (
                  <ContactHeader
                    contact={contact}
                    canDelete={data?.finance._policies.contactsDelete.can}
                    contactType={contactType}
                  />
                ),
                subNavigation: contact
                  ? [
                      {
                        id: 'overview',
                        title: i18n.t('finance.contacts.contact_details.tabs.overview'),
                        href: navigationRoot.contactId.overview.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: true,
                      },
                      {
                        id: 'details',
                        title: i18n.t('finance.contacts.contact_details.tabs.contact_details'),
                        href: navigationRoot.contactId.edit.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: true,
                      },
                      {
                        id: 'projects',
                        title: i18n.t('finance.contacts.contact_details.tabs.projects'),
                        href: navigationRoot.contactId.projects.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: canSeeProjectsPage,
                        variant: canSeeProjectsPageAsUpsell
                          ? ('upsell' as const)
                          : ('default' as const),
                      },
                      {
                        id: 'purchase_orders',
                        title: i18n.t('finance.contacts.contact_details.tabs.purchase_orders'),
                        href: navigationRoot.contactId.purchaseOrders.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: canSeePurchaseOrders,
                      },
                      {
                        id: 'invoices',
                        title: i18n.t('finance.contacts.contact_details.tabs.purchase_invoices'),
                        href: navigationRoot.contactId.invoices.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: canManagePurchaseFinancialDocuments,
                      },
                      {
                        id: 'deals',
                        title: i18n.t('finance.contacts.contact_details.tabs.deals'),
                        href: navigation.finance.sales.clients.contactId.deals.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: showDealsTab,
                        variant: canSeeDealsUpsell ? ('upsell' as const) : ('default' as const),
                      },
                      {
                        id: 'quotes',
                        title: i18n.t('finance.contacts.contact_details.tabs.quotes'),
                        href: navigation.finance.sales.clients.contactId.quotes.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: showQuotesTab,
                        variant: canSeeQuotesUpsell ? ('upsell' as const) : ('default' as const),
                      },
                      {
                        id: 'sales_invoices',
                        title: i18n.t('finance.contacts.contact_details.tabs.sales_invoices'),
                        href: navigation.finance.sales.clients.contactId.salesInvoices.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: showSaleInvoicesTab,
                        variant: canSeeSaleInvoicesUpsell
                          ? ('upsell' as const)
                          : ('default' as const),
                      },
                      {
                        id: 'documents',
                        title: i18n.t('finance.contacts.contact_details.tabs.files'),
                        href: navigationRoot.contactId.documents.fullPath({
                          contactId: contact.id,
                        }),
                        isVisible: true,
                      },
                    ]
                  : undefined,
              }
            }),
        },
        children: [
          {
            index: true,
            loader: ({ params }) => {
              return redirect(
                navigationRoot.contactId.overview.fullPath({
                  contactId: String(params.contactId),
                })
              )
            },
          },
          {
            path: navigationRoot.contactId.edit.path,
            element: <ContactEditPage />,
          },
          {
            path: navigationRoot.contactId.overview.path,
            element: <ContactsDetailsOverviewPage contactType={contactType} />,
          },
          {
            path: navigationRoot.contactId.projects.path,
            element: <ContactsDetailsProjectsPage />,
          },
          {
            path: navigationRoot.contactId.purchaseOrders.path,
            element: <ContactsDetailsPurchaseOrdersPage />,
          },
          ...(contactType === 'client'
            ? [
                {
                  path: navigation.finance.sales.clients.contactId.deals.path,
                  element: <ContactsDetailsDealsPage contactType={contactType} />,
                },
              ]
            : []),
          ...(contactType === 'client'
            ? [
                {
                  path: navigation.finance.sales.clients.contactId.quotes.path,
                  children: [
                    {
                      index: true,
                      element: <ContactsDetailsQuotesPage contactType={contactType} />,
                    },
                    clientQuoteRoute,
                  ],
                },
              ]
            : []),
          ...(contactType === 'client'
            ? [
                {
                  path: navigation.finance.sales.clients.contactId.salesInvoices.path,
                  element: <ContactsDetailsSaleInvoicesPage contactType={contactType} />,
                },
              ]
            : []),
          {
            path: navigationRoot.contactId.documents.path,
            element: <ContactsDetailsDocumentsPage />,
          },
          {
            path: navigationRoot.contactId.invoices.path,
            element: <ContactsDetailsInvoicesPage />,
          },
        ],
      },
    ],
  }

  return [contactsRootRoute]
}
